import * as Three from 'three';
import toast from 'react-hot-toast';
import { selectObjectInScene } from '../../pages/Dashboard/GridPage';
import { setLoadingMessage } from '../../redux/Slice/globalStates';
import {
  add_new_assets,
  addModel,
  addSceneMesh,
  selected_object,
  set_is_glb_image_editoropen,
  setPushIntersectedObject,
} from '../../redux/Slice/gridSlice';
import store from '../../redux/store';
import { getNextCopyName, buildTempFileName } from '../../utils/NamingUtils';
import { copyPhysicsToDuplicate } from '../../utils/Physics/PhysicsUtils';
import { UpdateMaterial } from './materialFunction';
import { SplatLoader } from '@sparkjsdev/spark';
import { disableSplatRaycast } from '../../utils/ThreeJsUtils/meshRaycastUtils';
import { applyStoredPivotToObject } from '../../utils/ThreeJsUtils/pivotEditor';

let splatMesh = null; // Keep a reference outside the function

function resolvePlySourceMesh(scene, item, originalMesh = null) {
  if (originalMesh) return originalMesh;
  if (!scene || !item) return null;

  const ids = [
    item._id,
    item.id,
    item.url,
    item.name,
    item.temp_file_name,
  ].filter(Boolean);

  for (const id of ids) {
    const obj = scene.getObjectByName(id);
    if (obj) return obj;
  }

  let found = null;
  scene.traverse((child) => {
    if (found || child.glb_type !== 'ply') return;
    if (ids.some((id) => child.name === id || child.url === id || child._id === id)) {
      found = child;
    }
  });
  return found;
}

function resolvePlyLoadUrl(item, sourceMesh) {
  const remotePath =
    item?.asset_file_path ||
    item?.asset_file_pathh ||
    sourceMesh?.asset_file_path ||
    sourceMesh?.asset_file_pathh;
  if (remotePath) {
    return { url: remotePath, revoke: false };
  }

  const file =
    sourceMesh?.originalFile ||
    sourceMesh?.objPath ||
    item?.originalFile ||
    item?.objPath;
  if (file instanceof File) {
    return { url: URL.createObjectURL(file), revoke: true };
  }

  return null;
}

export function handleMeshPlyUpload(
  e,
  {
    SplatLoader,

    scene,
    transformControls,
    selectedObject,
    setPolyCount,
  },
  setPendingUploads
) {
  if (!e.target.files || e.target.files.length === 0) {
    return;
  }

  const file = e.target.files[0];
  const maxSize = 100 * 1024 * 1024; // 100MB size limit

  if (file.size > maxSize) {
    toast.error(
      () => (
        <div className="flex justify-between items-center">
          <span>File size should not exceed 100MB.</span>
        </div>
      ),
      {
        duration: 3000, // auto close after 5 seconds
        style: {
          background: '#1d1e21',
          color: '#fff',
        },
      }
    );
    return;
  }

  selectedObject = null;
  store.dispatch(selected_object(null));

  store.dispatch(setLoadingMessage(true));
  const loader = new SplatLoader();
  const objectURL = URL.createObjectURL(file);
  const tempId = `temp-${Date.now()}-${file.name}`;

  loader.load(
    objectURL,
    function (ply) {
      const model = loader.parse(ply); // Parse packedSplats to get SplatMesh
      model.rotation.set(Math.PI, 0, 0); // Rotate 90 degrees on X-axis
      model.name = tempId;
      // model.userData = {
      //   id: tempId,
      //   fileName: file.name,
      //   fileSize: file.size,
      //   type: 'ply',n
      //   uploadedAt: new Date().toISOString(),
      // };

      // model.visible = true;
      model.objPath = file;
      model.objType = '3d';
      model._id = tempId;

      // model.originalFile = file;

      if (setPendingUploads) {
        setPendingUploads((prev) => [
          ...prev,
          {
            file: file,
            transform: {
              position_x: model.position.x || 0,
              position_y: model.position.y || 0,
              position_z: model.position.z || 0,
              rotation_x: model.rotation?.x || 0,
              rotation_y: model.rotation?.y || 0,
              rotation_z: model.rotation?.z || 0,
              scale_x: model.scale?.x || 1,
              scale_y: model.scale?.y || 1,
              scale_z: model.scale?.z || 1,
            },
            tempId: tempId,
          },
        ]);
      }

      // model.file_name = file.name;
      // model.file_size = file.size;
      model.glb_type = 'ply';
      model.originalFile = file;
      model.file_name = file.name;
      model.url = tempId;
      model.deleteType = 'mesh';
      disableSplatRaycast(model);

      store.dispatch(setPushIntersectedObject(model));
      scene.add(model);
      setPolyCount((prevCount) => prevCount + 1);
      store.dispatch(add_new_assets({ type: 'mesh', data: model }));
      store.dispatch(set_is_glb_image_editoropen(true));

      if (transformControls) {
        transformControls.attach(model);
      }

      selectObjectInScene(tempId);
      store.dispatch(addModel({ url: objectURL, name: file.name }));
      store.dispatch(addSceneMesh(model));
      store.dispatch(selected_object(model));
      store.dispatch(setLoadingMessage(false));

      toast.success(`Gaussian splat "${file.name}" loaded successfully!`);

      // Avoid forcing an extra render; main loop will render

      URL.revokeObjectURL(objectURL);
    },
    function (xhr) {
      if (xhr.lengthComputable) {
        const percent = (xhr.loaded / xhr.total) * 100;
      }
    },
    function (error) {
      console.error('Failed to load splat scene:', error);
      // toast.error(`Failed to load PLY mesh: ${error.message}`);
      store.dispatch(setLoadingMessage(false));
      URL.revokeObjectURL(objectURL);
    }
  );
}

export function DownloadMeshPly(
  meshData,
  { SplatLoader, Three, scene, selectedObject, setPolyCount }
) {
  meshData.forEach((mesh) => {
    const meshPath = mesh.asset_file_path;
    const position_x = mesh.position_x;
    const position_y = mesh.position_y;
    const position_z = mesh.position_z;
    const rotation_x = mesh.rotation_x;
    const rotation_y = mesh.rotation_y;
    const rotation_z = mesh.rotation_z;
    const scale_x = mesh.scale_x;
    const scale_y = mesh.scale_y;
    const scale_z = mesh.scale_z;
    
    // if(mesh.glb_type!=='ply')return;

    const loader = new SplatLoader();
    const objectURL = meshPath;
    store.dispatch(addModel({ url: objectURL, name: mesh.asset_file_path }));

    loader.load(
      objectURL,
      function (ply) {
        const model = loader.parse(ply); // Parse pa6ckedSplats to get SplatMesh

        // Always center PLY content first (matches initial import), then re-apply
        // any authored pivot offset so glyphs/splats don't snap onto the pivot.
        const box = new Three.Box3().setFromObject(model);
        const center = new Three.Vector3();
        box.getCenter(center);
        model.traverse((child) => {
          if (child.isMesh && child.geometry) {
            child.geometry.translate(-center.x, -center.y, -center.z);
          }
        });

        model.name = mesh._id;
        model.url = mesh._id;
        model._id = mesh._id;
        model.file_name = mesh.file_name || mesh.asset_file_name;
        model.deleteType = 'mesh';
        model.glb_type = 'ply';
        model.rotation.eulerOrder = 'XYZ';
        model.rotation.order = 'XYZ';
        model.asset_file_path = meshPath;
        disableSplatRaycast(model);

        model.position.set(position_x, position_y, position_z);
        model.rotation.set(
          Three.MathUtils.degToRad(rotation_x),
          Three.MathUtils.degToRad(rotation_y),
          Three.MathUtils.degToRad(rotation_z)
        );
        model.scale.set(scale_x, scale_y, scale_z);
        model.updateMatrix();
        model.updateMatrixWorld(true);

        model.userData = {};
        model.userData.material_settings = mesh.material_settings;
        model.objType = '3d';
        applyStoredPivotToObject(model, mesh, {
          projectId:
            typeof window !== 'undefined'
              ? window.location?.pathname?.split('/').filter(Boolean).pop()
              : null,
          cacheKeys: [
            mesh._id,
            mesh.id,
            mesh.name,
            mesh.temp_file_name,
            mesh.file_name,
            mesh.url,
          ],
        });

        const boundingBox = new Three.Box3().setFromObject(model);
        const size = new Three.Vector3();
        boundingBox.getSize(size);

        model.traverse(function (child) {
          if (child.isMesh) {
            child.updateMatrixWorld(true);
            child.castShadow = true;
            child.receiveShadow = true;

            if (child.material) {
              child.material.visible = true;
              child.material.needsUpdate = true;

              if (child.material.transparent) {
                child.material.opacity = 1.0;
                child.material.depthWrite = false;
              }

              child.material.side = Three.DoubleSide;
            }
          }
        });

        model.events = mesh.events;
        scene.add(model);
        selectedObject = model;

        setPolyCount((prevCount) => prevCount + 1);
        store.dispatch(setPushIntersectedObject(model));
        selectObjectInScene(mesh._id);
        store.dispatch(selected_object(model));
        store.dispatch(addSceneMesh(model));

        UpdateMaterial({ object: model, addData: mesh.material_settings });
      },
      function (xhr) {},
      function (error) {
        console.error('Error loading model:', error);
      }
    );
  });
}



export async function duplicateMeshPlyAsset(
  item,
  { Three, scene, transformControls, selectedObject, SplatLoader },
  setPendingUploads,
  originalMesh = null
) {
  const sourceMesh = resolvePlySourceMesh(scene, item, originalMesh);
  const fileName =
    item?.file_name ||
    sourceMesh?.file_name ||
    sourceMesh?.userData?.file_name ||
    'splat.ply';
  const loadSource = resolvePlyLoadUrl(item, sourceMesh);

  if (!loadSource?.url) {
    toast.error('Unable to duplicate PLY: missing file source');
    store.dispatch(setLoadingMessage(false));
    return;
  }

  selectedObject = null;
  store.dispatch(selected_object(null));
  transformControls?.detach();

  store.dispatch(setLoadingMessage(true));

  const baseName = fileName.replace(/\.[^/.]+$/, '');
  const ext = fileName.match(/\.[^/.]+$/)?.[0] || '.ply';
  const duplicateFileName = getNextCopyName(scene, baseName) + ext;
  const tempId = buildTempFileName(duplicateFileName);
  const duplicateMeshCode = `mesh_${Math.random().toString(36).substr(2, 6)}`;
  const assetPath =
    item?.asset_file_path ||
    item?.asset_file_pathh ||
    sourceMesh?.asset_file_path ||
    sourceMesh?.asset_file_pathh ||
    null;

  const loader = new SplatLoader();
  loader.load(
    loadSource.url,
    function (ply) {
      if (loadSource.revoke) {
        URL.revokeObjectURL(loadSource.url);
      }

      const duplicatedMesh = loader.parse(ply);

      const box = new Three.Box3().setFromObject(duplicatedMesh);
      const center = new Three.Vector3();
      box.getCenter(center);
      duplicatedMesh.traverse((child) => {
        if (child.isMesh && child.geometry) {
          child.geometry.translate(-center.x, -center.y, -center.z);
        }
      });

      duplicatedMesh.name = tempId;
      duplicatedMesh.url = tempId;
      duplicatedMesh._id = tempId;
      duplicatedMesh.file_name = duplicateFileName;
      duplicatedMesh.deleteType = 'mesh';
      duplicatedMesh.glb_type = 'ply';
      duplicatedMesh.objType = '3d';
      if (assetPath) {
        duplicatedMesh.asset_file_path = assetPath;
        duplicatedMesh.asset_file_pathh = assetPath;
      }
      duplicatedMesh.originalFile =
        sourceMesh?.originalFile || item?.originalFile || null;
      duplicatedMesh.objPath =
        sourceMesh?.objPath || item?.objPath || duplicatedMesh.originalFile;
      disableSplatRaycast(duplicatedMesh);

      const sourcePos = sourceMesh?.position;
      duplicatedMesh.position.set(
        (sourcePos?.x ?? item?.position_x ?? 0) + 0.5,
        sourcePos?.y ?? item?.position_y ?? 0,
        sourcePos?.z ?? item?.position_z ?? 0
      );

      if (sourceMesh?.rotation) {
        duplicatedMesh.rotation.copy(sourceMesh.rotation);
      } else {
        duplicatedMesh.rotation.set(
          Three.MathUtils.degToRad(item?.rotation_x || 0),
          Three.MathUtils.degToRad(item?.rotation_y || 0),
          Three.MathUtils.degToRad(item?.rotation_z || 0)
        );
      }

      if (sourceMesh?.scale) {
        duplicatedMesh.scale.copy(sourceMesh.scale);
      } else {
        duplicatedMesh.scale.set(
          item?.scale_x || 1,
          item?.scale_y || 1,
          item?.scale_z || 1
        );
      }

      // Store all item properties in userData
      duplicatedMesh.userData = {
        material_settings: { ...(item.material_settings || {}) },
        project: item.project,
        user: item.user,
        asset_file_name: item.asset_file_name,
        file_name: duplicateFileName,
        asset_file_type: item.asset_file_type,
        asset_file_path: assetPath,
        published: item.published,
        asset_thumbnail: item.asset_thumbnail,
        position_x: duplicatedMesh.position.x,
        position_y: duplicatedMesh.position.y,
        position_z: duplicatedMesh.position.z,
        scale_x: duplicatedMesh.scale.x,
        scale_y: duplicatedMesh.scale.y,
        scale_z: duplicatedMesh.scale.z,
        rotation_x: item.rotation_x || 0,
        rotation_y: item.rotation_y || 0,
        rotation_z: item.rotation_z || 0,
        events: [...(item.events || [])], // Copy events array
        saved: false, // Mark as unsaved since it's a duplicate
        mesh_code: duplicateMeshCode,
        originalId: item._id || item.id, // Reference to original item
        isDuplicate: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // // Apply material settings if they exist
      // if (item.material_settings) {
      //   duplicatedMesh.traverse((child) => {
      //     if (child.isMesh && child.material) {
      //       const material = child.material;
      //       const settings = item.material_settings;

      //       // Apply albedo (base color)
      //       if (settings.albedo && settings.albedo !== 'Select Value/Map') {
      //         if (settings.albedo.startsWith('#')) {
      //           material.color = new Three.Color(settings.albedo);
      //         }
      //       }

      //       // Apply metallic
      //       if (settings.metallic && settings.metallic !== 'Select Value/Map') {
      //         const metallicValue = parseFloat(settings.metallic);
      //         if (!isNaN(metallicValue)) {
      //           material.metalness = metallicValue;
      //         }
      //       }

      //       // Apply smoothness (roughness is inverse of smoothness)
      //       if (settings.smoothness && settings.smoothness !== 'Reflectivity') {
      //         const smoothnessValue = parseFloat(settings.smoothness);
      //         if (!isNaN(smoothnessValue)) {
      //           material.roughness = 1.0 - smoothnessValue;
      //         }
      //       }

      //       // Apply emission
      //       if (settings.emission && settings.emission !== 'Color/Texture') {
      //         if (settings.emission.startsWith('#')) {
      //           material.emissive = new Three.Color(settings.emission);
      //         }
      //       }

      //       // Apply transparency
      //       if (settings.transparency && settings.transparency !== 'Alpha') {
      //         const transparencyValue = parseFloat(settings.transparency);
      //         if (!isNaN(transparencyValue)) {
      //           material.opacity = transparencyValue;
      //           material.transparent = transparencyValue < 1.0;
      //         }
      //       }

      //       // Ensure material updates
      //       material.needsUpdate = true;
      //     }
      //   });
      // }

      // Handle pending uploads if needed
      if (setPendingUploads && originalMesh?.originalFile) {
        setPendingUploads((prev) => [
          ...prev,
          {
            file: originalMesh.originalFile,
            transform: {
              position_x: duplicatedMesh.position.x,
              position_y: duplicatedMesh.position.y,
              position_z: duplicatedMesh.position.z,
              rotation_x: item.rotation_x || 0,
              rotation_y: item.rotation_y || 0,
              rotation_z: item.rotation_z || 0,
              scale_x: duplicatedMesh.scale.x,
              scale_y: duplicatedMesh.scale.y,
              scale_z: duplicatedMesh.scale.z,
            },
            tempId: tempId,
            itemData: duplicatedMesh.userData, // Include all item data
          },
        ]);
      }

      scene.add(duplicatedMesh);

      store.dispatch(setPushIntersectedObject(duplicatedMesh));
      store.dispatch(addSceneMesh(duplicatedMesh));
      store.dispatch(add_new_assets({ type: 'mesh', data: duplicatedMesh }));
      copyPhysicsToDuplicate(
        resolvePlySourceMesh(scene, item, originalMesh) || originalMesh || item,
        duplicatedMesh
      );
      if (assetPath) {
        store.dispatch(addModel({ url: assetPath, name: duplicateFileName }));
      }

      toast.success(`PLY duplicated as "${duplicateFileName}"`);

      store.dispatch(set_is_glb_image_editoropen(true));
      selectObjectInScene(duplicatedMesh);
      if (transformControls) {
        transformControls.attach(duplicatedMesh);
      }
      store.dispatch(selected_object(duplicatedMesh));
      store.dispatch(setLoadingMessage(false));
    },
    undefined,
    function (error) {
      console.error('Error duplicating ply:', error);
      toast.error('Failed to duplicate ply asset');
      store.dispatch(setLoadingMessage(false));
    }
  );
}
